// Reception lookup by sensitive fields (PB-06-02, PD-492/PD-493): vehicle plate and permitted
// identity-document number, each behind its own field-level grant, with masked matches only.
// The raw plate or number is never returned, stored or logged; it is compared through the plate
// registry's folded form and the module 08 keyed HMAC index (PD-194).
import { z } from 'zod';
import type { BoardItem } from './visits.ts';
import { Jurisdiction } from './vehicles.ts';

export const LOOKUP_FIELDS = ['any', 'plate', 'document'] as const;
export type LookupField = (typeof LOOKUP_FIELDS)[number];

export const ReceptionLookupRequest = z.object({
  siteId: z.uuid(),
  query: z.string().trim().min(2, 'v.tooShort').max(300, 'v.tooLong'),
  /** 'any': pass link, reference, card, name/host/organisation/title (the default, unchanged). */
  field: z.enum(LOOKUP_FIELDS).default('any'),
  /** Plate jurisdiction (e.g. AE-DU) to narrow a plate search. */
  plateJurisdiction: Jurisdiction.optional(),
  /** Document type code from the organisation's verification settings; omitted = every enabled type. */
  documentType: z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey').optional(),
  /** Issuing country (ISO 3166-1 alpha-2) for types accepted from any country, such as passports. */
  documentJurisdiction: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, 'v.jurisdiction').optional(),
});
export type ReceptionLookupRequest = z.infer<typeof ReceptionLookupRequest>;

/** Why a row matched a sensitive search: the field and a masked value, never the value itself. */
export interface LookupMatchDto {
  readonly participationId: string;
  readonly field: 'plate' | 'document';
  /** Plate: first character and last two (`D•••45`); document: last four (`•••• 1234`). */
  readonly masked: string;
  /** Plate only: false when the plate matched after folding confusable characters (O/0, I/1, …). */
  readonly exact: boolean;
}

export interface ReceptionLookupResponse {
  readonly items: readonly BoardItem[];
  readonly rejected: 'link_invalid' | null;
  readonly matches: readonly LookupMatchDto[];
}
